<template>
  <div class="checkout-page" v-loading="bLoading" :style="pageStyles">
    <div v-if="bCreateOrderError || bCheckoutComplete || bUpdateOrderError">
      <template v-if="bCreateOrderError">
        <ErrorMessage
          v-if="bCreateOrderError"
          title="Well, this is embarrassing"
          :message="strCreateOrderErrorMessage"
        >
          <slot name="messageSuffix">
            <el-button
              type="text"
              class="mt-4"
              style="padding:0"
              @click="goHome()"
            >
              Back to cart
            </el-button>
          </slot>
        </ErrorMessage>
      </template>
      <template v-else-if="bUpdateOrderError">
        <ErrorMessage
          v-if="bUpdateOrderError"
          title="Well, this is unfortunate"
          :message="strUpdateOrderErrorMessage"
        >
          <slot name="messageSuffix">
            <el-button
              type="text"
              class="mt-4"
              style="padding:0"
              @click="goHome()"
            >
              Back to cart
            </el-button>
          </slot>
        </ErrorMessage>
      </template>
      <template v-else-if="bCheckoutComplete">
        <PaymentSuccessfulMessage
          :isConfirmedOrder="bIsPaymentConfirmed"
          :orderConfirmationNumber="orderConfirmationNumber"
          :customerEmail="orderEmail"
        >
        </PaymentSuccessfulMessage>
      </template>
    </div>
    <div v-else-if="!bLoading" class="container">
      <h2
        :style="{ paddingBottom: isOrderDeliveryStateValid ? '30px' : '0px' }"
      >
        Checkout
      </h2>
      <div class="row">
        <template v-if="isOrderValid">
          <div class="col-md-6 order-md-1 ">
            <h4 class="checkout-header">Order Summary</h4>
            <OrderSummary
              style="margin-top: 17px"
              :order="getOrder"
              v-if="hasOrder"
              :showShippingLimitWarnings="checkoutStep === 'address'"
              :showServiceFees="showServiceFees"
              :serviceFeeLabel="getServiceFeeLabel"
            ></OrderSummary>
          </div>
        </template>
        <div class="col-md-6" v-if="checkoutStep === 'address'">
          <!--
            if the order contains mixed delivery modes (which we don't support),
            we hide everything and show a blocking warning message
            -->
          <div v-if="!isOrderDeliveryStateValid">
            <div class="ui yellow message">
              <div>
                <el-icon name="warning-outline mr-1"></el-icon> Sorry, this
                order cannot be placed because it contains books with mixed
                delivery methods. For example, some books are for school pickup,
                others are for home delivery.
              </div>
              <p class="mt-2">
                To fix this error, return to your cart and create separate
                orders for books you want shipped and books you will pickup from
                the school.
              </p>
            </div>
            <div style="float:left">
              <el-button type="text" @click="onClickReturnToCart"
                ><el-icon name="arrow-left"></el-icon> Return to cart</el-button
              >
            </div>
          </div>
          <template v-if="isOrderValid">
            <template v-if="hasOrder">
              <h4 class="checkout-header">Delivery Method</h4>
              <DeliveryMethod
                class="mt-3 mb-4"
                v-if="hasOrder"
                :order="getOrder"
                :canChangeDeliveryMethod="canChangeDeliveryMethod"
                v-on:deliveryMethodChanged="onDeliveryMethodChange"
              >
              </DeliveryMethod>
            </template>
            <template v-if="isShipped">
              <div class="mb-4">
                <h4 class="checkout-header">Shipping address</h4>
                <shipping-address-form
                  :disableForm="bDisableOrderShippingForm"
                  :defaultShippingName="getFormName"
                  :autoFocusForm="isShipped"
                >
                </shipping-address-form>
              </div>
            </template>
            <h4 class="checkout-header">Billing address</h4>
            <OrderAddressForm
              style="margin-top: 30px"
              v-on:evt-address-form-valid="onAddressStepSubmitted"
              :name="getFormName"
              :email="getFormEmail"
              :address_line_1="getFormAddressLine1"
              :address_line_2="getFormAddressLine2"
              :address_city="getFormAddressCity"
              :address_state="getFormAddressState"
              :address_zip="getFormAddressZip"
              :address_country="getFormAddressCountry"
              :disableForm="bDisableOrderAddressForm"
              :autoFocusForm="!isShipped"
            >
              <template v-slot:footer>
                <h4 class="checkout-header mt-4">My kids</h4>
                <MyKidsWidget
                  :kids="getOrderKids"
                  :orderId="getOrderId"
                ></MyKidsWidget>
              </template>
            </OrderAddressForm>
          </template>
        </div>
        <div class="col-md-6" v-else-if="checkoutStep === 'payment'">
          <h4 class="checkout-header">Address Summary</h4>
          <ContactAddressSummaryWidget
            :email="getOrderEmail"
            :address="getOrderAddressCondensed"
            :shippingAddress="
              isShipped ? getOrderShippingAddressCondensed : null
            "
            :countKids="getOrderKids.length"
            v-on:evt-request-to-change-address-info="
              onEventRequestToChangeAddressInfo
            "
          ></ContactAddressSummaryWidget>
          <h4 class="checkout-header" style="padding-top: 10px">Payment</h4>
          <p style="padding: 20px 0">
            <el-icon name="lock mr-2"></el-icon>All transactions are secure and
            encrypted.
          </p>
          <StripeCreditCardInput
            :paymentIntentClientSecret="getOrderStripePaymentIntentClientSecret"
            :buttonDisplayText="getStripePaymentButtonText"
            :billingAddress="getOrderAddress"
            :orderId="getOrderId"
            :stripe-account-id="getStripeAccountId"
            v-on:evt-order-pre-payment-validation-failure="
              onOrderPrePaymentValidationFailed
            "
            v-on:evt-payment-request-start="onPaymentRequestStart"
            v-on:evt-payment-request-finish="onPaymentRequestFinish"
            v-on:evt-payment-success="onPaymentSuccess"
          ></StripeCreditCardInput>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import ErrorMessage from "@/modules/shop/messages/ErrorMessage.vue";
import PaymentSuccessfulMessage from "@/modules/shop/messages/PaymentSuccessfulMessage.vue";
import OrderSummary from "./components/checkout/OrderSummary";
import OrderAddressForm from "./components/checkout/OrderAddressForm";
import ContactAddressSummaryWidget from "./components/checkout/ContactAddressSummaryWidget";
import StripeCreditCardInput from "./components/checkout/StripeCreditCardInput";
import { httpStatusException } from "../../api";
import MyKidsWidget from "./components/checkout/MyKidsWidget";
import DeliveryMethod from "@/modules/shop/components/checkout/DeliveryMethod";
import ShippingAddressForm from "@/modules/shop/components/checkout/ShippingAddressForm";

export default {
  components: {
    ShippingAddressForm,
    DeliveryMethod,
    MyKidsWidget,
    StripeCreditCardInput,
    ContactAddressSummaryWidget,
    OrderSummary,
    ErrorMessage,
    PaymentSuccessfulMessage,
    OrderAddressForm
  },
  data() {
    return {
      bLoading: true,
      orderConfirmationNumber: null,
      bCreateOrderError: false, // show a blocking error message if creating the order fails
      bUpdateOrderError: false, // show a blocking error message if updating the order fails
      strCreateOrderErrorMessage: "", // message to show if creating the order fails
      strUpdateOrderErrorMessage: "", // message to show if updating the order fails
      bDisableOrderAddressForm: false, // disable the address form (used when updating the address via the API)
      bDisableOrderShippingForm: false, // disable the shipping form (used when updating the address via the API)
      bCheckoutComplete: false, // checkout is done, show success message
      bIsPaymentConfirmed: false, // used to style the confirmation message. Based upon stripe payment intent status of: succeeded|processing
      checkoutStep: "address", // switch the checkout step: address|payment
      orderEmail: null // the final email address of the order. Local state - used to pass down to payment success message
    };
  },
  props: {},
  computed: {
    // map store state to local computed properties:
    ...mapState({
      // id: state => state.book.id
    }),
    // map store getters to local computed properties:
    ...mapGetters([
      "hasOrder",
      "getStorefrontShortcode",
      "getCartItemsState",
      "getCountOfItemsInCart",
      "getOrder",
      "getOrderKids",
      "getUserDisplayName",
      "getUserEmail",
      "getOrderId",
      "getOrderEmail",
      "getOrderAddressCondensed",
      "getOrderShippingAddressCondensed",
      "getOrderAddress",
      "getOrderStripePaymentIntentClientSecret",
      "getOrderInvoiceNumber",
      "getServiceFeeLabel",
      "showServiceFees"
    ]),
    pageStyles() {
      let styles = {};
      if (
        this.bCreateOrderError ||
        this.bCheckoutComplete ||
        this.bUpdateOrderError
      ) {
        styles = {
          flex: "1",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        };
      }
      return styles;
    },
    getStripePaymentButtonText() {
      const order = this.getOrder;
      return `Pay ${order.display_total}`;
    },
    oOrderAddress() {
      const order = this.getOrder;
      return order.billing_address || {};
    },
    oShippingAddress() {
      const order = this.getOrder;
      return order.shipping_address || {};
    },
    getStripeAccountId() {
      const order = this.getOrder;
      return order.stripe_account_id;
    },
    getFormName() {
      // use the name from an order if it exists
      // otherwise, default to the user's name on file
      const order = this.getOrder;
      if (order.id && order.billing_address && order.billing_address.name) {
        return order.billing_address.name;
      } else {
        return this.getUserDisplayName;
      }
    },
    getFormEmail() {
      // use the email from an order if it exists
      // otherwise, default to the user's email on file
      const order = this.getOrder;
      if (order.id && order.billing_address && order.billing_address.email) {
        return order.billing_address.email;
      } else {
        return this.getUserEmail;
      }
    },
    getFormAddressLine1() {
      return this.oOrderAddress.address_line_1;
    },
    getFormAddressLine2() {
      return this.oOrderAddress.address_line_2;
    },
    getFormAddressCity() {
      return this.oOrderAddress.address_city;
    },
    getFormAddressState() {
      return this.oOrderAddress.address_state;
    },
    getFormAddressZip() {
      return this.oOrderAddress.address_zip;
    },
    getFormAddressCountry() {
      return this.oOrderAddress.address_country;
    },
    // this flag is false when the order contains items with
    // mixed delivery modes (e.g. pickup, shipping)
    // we don't support this and show a blocking error message instead
    isOrderDeliveryStateValid() {
      return this.order.is_delivery_state_valid;
    },
    // can the user change their delivery method?
    // this is only truthy if all items in the cart support
    // both pickup and delivery
    canChangeDeliveryMethod() {
      const order = this.getOrder;
      return order.can_delivery_method_be_changed;
    },
    order() {
      return this.getOrder || {};
    },
    isForSchoolPickup() {
      return this.order.is_for_school_pickup;
    },
    isShipped() {
      return this.order.is_shipped;
    },
    isOrderValid() {
      return this.isOrderDeliveryStateValid;
    },
    ...mapState({
      oShippingAddress: state => state.shop.forms.shippingForm.formAttributes
    })
  },
  methods: {
    // map component methods to store.dispatch calls
    ...mapActions([
      "createOrder",
      "updateOrderItems",
      "updateOrderAddress",
      "updateDeliveryMethod",
      "updateShippingAddress",
      "setFullScreenLoading",
      "resetCartState",
      "resetOrderState",
      "resetInventoryState",
      "getStorefrontCatalog"
    ]),
    onOrderPrePaymentValidationFailed() {
      // The API call to validate the product prior to payment failed.
      // This is very unlikely, but it can happen if the stock levels/product availability
      // changed in between the time the user went from Address > Payment.
      // Here, we're simply going to abort with an error message
      console.log(`[Checkout] Order pre payment validation failed!`);
      this._resetStoreState();
      this.setFullScreenLoading(false);
      this.strUpdateOrderErrorMessage =
        "We're really sorry, but there's not enough stock left for one or more items in your cart. Your card has not been charged.";
      this.bUpdateOrderError = true;
    },
    onPaymentRequestStart() {
      // Block the entire UI when the payments request starts.
      // This prevents bad stuff like logout, changing the order info etc.
      this.setFullScreenLoading(true);
    },
    onPaymentRequestFinish() {
      // unblock the UI when the payment request finishes
      this.setFullScreenLoading(false);
    },
    onPaymentSuccess(strPaymentStatus) {
      // payment is successful
      // the status will either equal: succeeded|processing
      console.log(`[Checkout] Payment success! Status: ${strPaymentStatus}`);
      // this just controls the style of confirmation message we show the user
      this.bIsPaymentConfirmed = strPaymentStatus === "succeeded";
      this.bCheckoutComplete = true;
      this.orderConfirmationNumber = this.getOrderInvoiceNumber;
      // we store the email locally since it's used in the payment success
      // component and we're about to reset order state
      this.orderEmail = this.getOrderEmail;
      // reset the cart/inventory and catalog
      this._resetStoreState();
    },
    onEventRequestToChangeAddressInfo() {
      this.checkoutStep = "address";
    },
    onAddressStepSubmitted: async function(inOAddressForm) {
      this.bDisableOrderAddressForm = true;
      this.bDisableOrderShippingForm = true;
      try {
        // update the order address via the API
        await this.updateOrderAddress({
          orderId: this.getOrderId,
          oAddress: inOAddressForm
        });
        if (this.isShipped) {
          await this.updateShippingAddress({
            orderId: this.getOrderId,
            oAddress: this.oShippingAddress
          });
        }
        // if successful, proceed to payment
        this.checkoutStep = "payment";
      } catch (err) {
        console.log(err);
        this.$notify.error({
          title: "Checkout error",
          message: "An error occurred saving your order. Please try again"
        });
      } finally {
        this.bDisableOrderAddressForm = false;
        this.bDisableOrderShippingForm = false;
      }
    },
    async onDeliveryMethodChange(inDeliveryMethod) {
      this.bLoading = true;
      let isShipped, isPickup;
      console.log("onDeliveryMethodChange");
      console.log(inDeliveryMethod);
      if (inDeliveryMethod === "shipped") {
        isShipped = true;
        isPickup = false;
      } else {
        isShipped = false;
        isPickup = true;
      }
      try {
        await this.updateDeliveryMethod({
          orderId: this.getOrderId,
          isShipped,
          isPickup
        });
      } catch (err) {
        console.log(err);
        this._resetStoreState();
        this.strCreateOrderErrorMessage =
          "Something went trying to checkout your order. Please try again.";
        this.bUpdateOrderError = true;
      } finally {
        this.bLoading = false;
      }
    },
    createOrderFromCartItems: async function() {
      this.bLoading = true;
      this.bCreateOrderError = false;
      try {
        await this.createOrder(this.getCartItemsState);
      } catch (err) {
        // When the user goes to checkout, we know that they are now authenticated.
        // We attempt to create an order from their cart items, and display the first step of the checkout flow.

        // If this API call fails, it's normally because a product is out of stock or no longer for sale.
        // Our brute force solution to this is to simply disregard the current order
        // and re-fetch the store catalogue - and ask the user to shop again.

        // It's not pretty, but it's also not a very likely scenario.
        // There are obviously better solutions to stock control, but all of
        // them introduce more complexity to the cart.
        this._resetStoreState();
        this.strCreateOrderErrorMessage =
          "Something went trying to checkout your order. Please try again.";
        if (err instanceof httpStatusException) {
          if (err.errorType === "PRODUCT_OUT_OF_STOCK") {
            this.strCreateOrderErrorMessage =
              "We're really sorry, but there's not enough stock left for one or many items in your cart.";
          } else if (err.errorType === "PRODUCT_NOT_FOR_SALE") {
            this.strCreateOrderErrorMessage =
              "One or more items in your cart are no longer for sale.";
          } else if (err.errorType === "UNKNOWN_ERROR") {
            this.strCreateOrderErrorMessage =
              "An unknown error occurred trying to checkout your order. Please try again.";
          }
        }
        this.bCreateOrderError = true;
      } finally {
        this.bLoading = false;
      }
    },
    updateOrderFromCartItems: async function() {
      this.bLoading = true;
      this.bCreateOrderError = false;
      try {
        await this.updateOrderItems({
          orderId: this.getOrderId,
          cartItems: this.getCartItemsState
        });
      } catch (err) {
        // If this API call fails, it's normally because the product is now out of stock.
        // Our brute force solution to this is to simply disregard the current order
        // and re-fetch the store catalogue - and ask the user to shop again.

        // It's not pretty, but it's also not a very likely scenario.
        // There are obviously better solutions to stock control, but all of
        // them introduce more complexity to the cart.
        this._resetStoreState();
        this.strUpdateOrderErrorMessage =
          "An error occurred trying to checkout your order. Please try again.";
        if (err instanceof httpStatusException) {
          if (err.errorType === "PRODUCT_OUT_OF_STOCK") {
            this.strUpdateOrderErrorMessage =
              "One or more items in your cart just sold out - they might come back in stock later.";
          } else if (err.errorType === "PRODUCT_NOT_FOR_SALE") {
            this.strUpdateOrderErrorMessage =
              "One or more items in your cart are no longer for sale.";
          }
        }
        this.bUpdateOrderError = true;
      } finally {
        this.bLoading = false;
      }
    },
    onClickReturnToCart() {
      this.$router.push({ name: "storefront" });
    },
    init() {
      if (!this.hasOrder) {
        if (!this.getCountOfItemsInCart || this.getCountOfItemsInCart === 0) {
          console.warn(
            `[Checkout] Found no items in the cart and no existing order. Redirecting to storefront`
          );
          this.$router.push({ name: "storefront" });
        } else {
          console.log(
            `[Checkout] Items in the cart - no order exists. Creating order...`
          );
          this.createOrderFromCartItems();
        }
      } else {
        console.log(`[Checkout] Found existing order. Updating items.`);
        // the user left checkout and maybe modified their cart
        // update the existing order
        this.updateOrderFromCartItems();
      }
    },
    goHome() {
      this.$router.push({ name: "storefront" });
    },
    _resetStoreState() {
      // reset the cart/order/inventory state
      this.resetCartState();
      this.resetOrderState();
      this.resetInventoryState();
    }
  },
  mounted() {
    this.init();
  }
};
</script>
<style>
.checkout-page {
  height: 100%;
  margin-bottom: 30px;
  margin-top: 30px;
}
.checkout-header {
  border-bottom: 1px solid #cacaca;
  padding-bottom: 10px;
}
</style>
